import { useCallback } from 'react'

import { create } from '@bufbuild/protobuf'
import { createClient } from '@connectrpc/connect'
import {
  createConnectQueryKey,
  useMutation,
  useTransport,
} from '@connectrpc/connect-query'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { SplitSupplyNoticeSection } from '@ebalance/proto/bffuser/v1/split_supply_plan_model_pb'
import {
  getOwnSplitSupplyNotice,
  importOwnSplitSupplyNotices,
  issueOwnSplitSupplyNoticeUploadUrl,
  listOwnSplitSupplyNotices,
  previewOwnSplitSupplyNoticeImport,
} from '@ebalance/proto/bffuser/v1/split_supply_plan_service-SplitSupplyPlanService_connectquery'
import {
  DownloadOwnSplitSupplyNoticeCsvTemplateRequestSchema,
  SplitSupplyPlanService,
} from '@ebalance/proto/bffuser/v1/split_supply_plan_service_pb'
import { useTranslation } from 'react-i18next'

import { useErrorToast } from '~/hooks/useErrorToast'
import {
  type InvalidatingMutationResultFor,
  useInvalidatingMutation,
} from '~/hooks/useInvalidatingMutation'
import { downloadBytes } from '~/libs/downloadBytes'
import { type CivilDate, civilDateToProtoDate } from '~/models/civilDate'
import { uploadNoticeImportFile } from '~/pages/SplitSupplyPlan/hooks/uploadNoticeImportFile'
import { describeSplitSupplyNoticeError } from '~/pages/SplitSupplyPlan/models/serverError'

/**
 * ファイル 1 つを受け取り先 (署名つき URL) へ上げて、 自社の通告値の取込の token を返す関数を返す
 * (手順は `uploadNoticeImportFile`)。 他社の取込の token とは別の種類で、 取り違えて使えない。
 */
export function useUploadOwnNoticeFile(): (file: File) => Promise<string> {
  const { mutateAsync: issueUploadUrl } = useMutation(
    issueOwnSplitSupplyNoticeUploadUrl,
  )
  return (file) =>
    uploadNoticeImportFile(file, (fileName) => issueUploadUrl({ fileName }))
}

/**
 * 取り込む内容の確認 (preview)。 何も書かず、 token も使い切らない (確認し直せる)。
 * 中身の問題 (誤り・取り込める地点が無い・上限超え) は成功の response に載って戻る。
 */
export function usePreviewOwnNoticeImport(): InvalidatingMutationResultFor<
  typeof previewOwnSplitSupplyNoticeImport
> {
  return useMutation(previewOwnSplitSupplyNoticeImport)
}

/**
 * 確認した内容の取り込み。 成功したら自社の通告値の一覧と詳細を最新化する。
 */
export function useImportOwnSplitSupplyNotices(): InvalidatingMutationResultFor<
  typeof importOwnSplitSupplyNotices
> {
  return useInvalidatingMutation(importOwnSplitSupplyNotices, [
    createConnectQueryKey({
      schema: listOwnSplitSupplyNotices,
      cardinality: 'finite',
    }),
    createConnectQueryKey({
      schema: getOwnSplitSupplyNotice,
      cardinality: 'finite',
    }),
  ])
}

/**
 * 取込用の CSV テンプレートのダウンロード hook。 `DownloadOwnSplitSupplyNoticeCsvTemplate` を押下時に
 * 命令的に呼び、 レスポンスの content を Blob 経由でブラウザダウンロードする。 ファイル名は
 * レスポンスの fileName を使い、 frontend で組み立てない。 列 (自社が通告値を出す地点) と
 * 取り込めない期間の判断は backend が持つ。
 */
export function useDownloadOwnNoticeCsvTemplate(): {
  download: (input: {
    section: SplitSupplyNoticeSection
    targetDate: CivilDate
    areaCode: AreaCode | undefined
  }) => Promise<void>
} {
  const { t } = useTranslation()
  const transport = useTransport()
  const showErrorToast = useErrorToast()

  const download = useCallback(
    async (input: {
      section: SplitSupplyNoticeSection
      targetDate: CivilDate
      areaCode: AreaCode | undefined
    }): Promise<void> => {
      const client = createClient(SplitSupplyPlanService, transport)
      try {
        const res = await client.downloadOwnSplitSupplyNoticeCsvTemplate(
          create(DownloadOwnSplitSupplyNoticeCsvTemplateRequestSchema, {
            section: input.section,
            targetDate: civilDateToProtoDate(input.targetDate),
            areaCode: input.areaCode,
          }),
        )
        downloadBytes(res.content, res.contentType, res.fileName)
      } catch (err) {
        showErrorToast({
          title: t('page:splitSupplyPlan.own.import.template.errorTitle'),
          description: describeSplitSupplyNoticeError(err, t),
        })
      }
    },
    [transport, t, showErrorToast],
  )

  return { download }
}
